import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { LoggerModule, NgxLoggerLevel } from 'ngx-logger';
import { env } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CrisprModule } from './crispr/crispr.module';
import { ExchangeModule } from './exchange/exchange.module';
import { LayoutModule } from './layout/layout.module';
import { LocaleService } from './locale.service';
import { MySwarmModule } from './my-swarm/my-swarm.module';
import { NarwhalService } from './narwhal.service';
import { PopulationModule } from './population/population.module';
import { ProfileComponent } from './profile/profile.component';
import { RegisterComponent } from './register/register.component';
import { AuthGuard } from './shared/auth/auth-guard.service';
import { AuthInterceptor } from './shared/auth/auth.interceptor';
import { AuthService } from './shared/auth/auth.service';
import { AuthExpiredInterceptor } from './shared/interceptor/auth-expired.interceptor';
import { ErrorHandlerInterceptor } from './shared/interceptor/errorhandler.interceptor';
import { NotificationInterceptor } from './shared/interceptor/notification.interceptor';
import { SharedModule } from './shared/shared.module';

export function HttpLoaderFactory(http: HttpClient) {
	return new TranslateHttpLoader(http, './assets/i18n/');
}

export function getLocale(localeService: LocaleService) {
	return localeService.locale;
}

@NgModule({
	declarations: [AppComponent, ProfileComponent, RegisterComponent],
	imports: [
		BrowserModule,
		BrowserAnimationsModule,
		HttpClientModule,
		AppRoutingModule,
		SharedModule,
		PopulationModule,
		MySwarmModule,
		CrisprModule,
		ExchangeModule,
		LayoutModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient]
			}
		}),
		LoggerModule.forRoot({
			level: env.logLevel,
			serverLogLevel: NgxLoggerLevel.OFF
		})
	],
	providers: [
		AuthService,
		AuthGuard,
		NarwhalService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthInterceptor,
			multi: true
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: AuthExpiredInterceptor,
			multi: true,
			deps: [Injector]
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: ErrorHandlerInterceptor,
			multi: true,
			deps: [Injector]
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: NotificationInterceptor,
			multi: true,
			deps: [Injector]
		},
		{ provide: LOCALE_ID, useFactory: getLocale, deps: [LocaleService] }
	],
	bootstrap: [AppComponent]
})
export class AppModule {
	constructor(private translate: TranslateService, private localeService: LocaleService) {
		const currentLocale = localeService.locale;
		// register locale for i18n pipes
		import(/* webpackInclude: /(en|de)\.js$/ */ `@angular/common/locales/${currentLocale}.js`)
			.then(locale => {
				registerLocaleData(locale.default);
			});
		translate.use(currentLocale);
	}
}